import {Edge, Node} from 'reactflow';

import {DEFAULT_LAYOUT_ALGORITHM, LayoutAlgorithmName, getLayoutAlgorithm} from './algorithms';
import type {FlowchartData} from './algorithms';
import calculateDimensionsForLayout, {
  applyCustomLayoutSettings,
  CustomLayoutSettings,
  DimensionsForLayout,
} from './calculateDimensionsForLayout';

/**
 * Arrange nodes for a `reactflow` flowchart, based on pre-calculated dimensions of the nodes and edge labels.
 *
 * See `algorithms.ts` for more information on layout algorithms.
 *
 * @param flowchartData The nodes and edges for the `reactflow` flow chart.
 * @param dimensions Calculated dimensions of each nodes and edge, and spacing between nodes
 * @param algorithm The layout algorithm to use. Defaults to `dagre`.
 * @returns A copy of `flowchartData`, with `position` updated on the nodes
 *
 * @package
 */
export async function layoutFlowchartFromDimensions<N extends Node, E extends Edge>(
  {nodes, edges, getHandles}: FlowchartData<N, E>,
  dimensions: DimensionsForLayout,
  algorithm: LayoutAlgorithmName = DEFAULT_LAYOUT_ALGORITHM
): Promise<FlowchartData<N, E>> {
  return await getLayoutAlgorithm<N, E>(algorithm)({nodes, edges, getHandles}, dimensions);
}

/**
 * Arrange nodes for a `reactflow` flowchart, by calculating the dimensions' of the nodes and edge labels.
 *
 * Returns positions using the given node origin as the reference point (i.e. [0,0] means each position is the
 * top left of the node). Reactflow uses a default of [0,0].
 *
 * To prevent new nodes or edges from showing until their positions have been calculated, initialize the new
 * nodes and edges with `hideFlowchartElementBeforeLayout`. `layoutFlowchart` will automatically un-hide all
 * nodes and edges, after calculating their positions.
 *
 * @param flowchartData The nodes and edges for the `reactflow` flow chart.
 * @param customLayoutSettings Custom layout settings. See `DEFAULT_LAYOUT_SETTINGS` for defaults. By default,
 *    nodes' actual heights and widths, which are automatically calculated by `reactflow`, are used.
 * @param algorithm The layout algorithm to use. Defaults to `dagre`.
 * @returns A copy of `flowchartData`, with `position` updated on the nodes
 */
export default async function layoutFlowchart<N extends Node, E extends Edge>(
  {nodes, edges, getHandles}: FlowchartData<N, E>,
  customLayoutSettings: CustomLayoutSettings<N, E> = {}
): Promise<FlowchartData<N, E>> {
  const layoutSettings = applyCustomLayoutSettings(customLayoutSettings);
  return await layoutFlowchartFromDimensions(
    {nodes, edges, getHandles},
    calculateDimensionsForLayout(nodes, edges, layoutSettings),
    layoutSettings.algorithm
  );
}
